import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { environment } from "../../environments/environment";
import { config } from "../../environments/config";
import { HttpService } from "./http.service";
import * as XLSX from "xlsx";
import { LocalStorageService } from "./local-storage.service";

const BASE_URL = environment.baseUrl;
const EXP_BASE_URL = environment.baseUrlV2;
const EXP_BASE_URL_V3 = environment.baseUrlV3;
const EXPURL = environment.expurl;
const USERURL = environment.userUrl;
const ACCESS_TOKEN_EXPIRY_DURATION = 5;
const USERS_V3_URL = environment.userUrlV3;
const GO_PROCESS_URL = environment.goProcess;
const PHOBASEURL = environment.phoBaseUrl;
const PHOBASEURL_V2 = environment.phoBaseUrlV2;
const PHOBASEURL_V3 = environment.phoBaseUrlV3;
const GOPROCESSSTATES = environment.goProcessStates;

const apiData = {
  user: {
    me: `${USERURL}me`,
    meV3: `${USERS_V3_URL}users/me`,
    login: `${USERURL}login`,
    users: `${USERURL}users/`,
    acceptTerms: `${USERURL}/user/termscondition`,
    usersByID: `${EXP_BASE_URL}/users/`,
    getUser: `${USERURL}user/search`,
    searchUserV3: `${USERS_V3_URL}users/search`,
    logout: `${USERURL}logout`,
    refreshToken: `${USERURL}refresh_access_token`,
    newsletter: `${USERURL}newsletter`,
    approval: `${USERURL}user/approval`,
    approvalV3: `${USERS_V3_URL}users/onboard`,
    sharedWith: `${USERURL}bulk/users/detail`,
    countriesList: `${USERS_V3_URL}countries`,
    countriesListWithZone: `${USERS_V3_URL}countries/zone/list`,
  },
  roles: {
    permission_mappings: `${USERS_V3_URL}roles/permissions/mapping`,
  },
  projects: {
    projects: `${BASE_URL}projects`,
    keyaccounts: `${URL}key_account_by_country`,
    regions: `${URL}regions_by_key_account_and_country`,
    createkeyaccounts: `${URL}key_accounts`,
    createregions: `${URL}regions`,
    zone: `${URL}zones`,
    users: `${USERURL}users`,
    requests: `${USERURL}users/approvals`,
    approval: `${USERURL}user/approvals`,
    bulkApproval: `${USERURL}users/approvals/bulk`,
    status: `${USERURL}user/status`,
  },
  admin: {
    zones: `${USERS_V3_URL}zones`,
    keyAccounts: `${USERS_V3_URL}key-accounts`,
    countries: `${USERS_V3_URL}countries`,
    userRequests: `${USERS_V3_URL}users/requests`,
    users: `${USERS_V3_URL}admin/users`,
    onboardUser: `${USERS_V3_URL}users/onboard`,
    updateZone: (zoneId: string) => `${USERS_V3_URL}zones/${zoneId}`,
    deleteZone: (zoneId: string) => `${USERS_V3_URL}zones/${zoneId}`,
    updateKeyAccount: (keyAccountId: string) =>
      `${USERS_V3_URL}key-accounts/${keyAccountId}`,
    getUsers: (zoneId: string) => `${USERS_V3_URL}zones/${zoneId}/users`,
    getStatus: (userId: string) => `${USERS_V3_URL}users/${userId}/status`,
    updateUser: (userId: string) => `${USERS_V3_URL}users/${userId}`,
    getRequests: (zoneId: string) => `${USERS_V3_URL}zones/${zoneId}/requests`,
    getExperiments: (zoneId: string) =>
      `${EXP_BASE_URL_V3}/zones/${zoneId}/experiments`,
    getKeyAccounts: (zoneId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/key-accounts`,
    migrateZone: (zoneId: string, userId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/users/${userId}/migrate`,
    revokeZone: (zoneId: string, userId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/users/${userId}/revoke`,
    approveRejectRequest: (zoneId: string, requestId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/requests/${requestId}`,
    syncKeyAccounts: (zoneId: string, userId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/users/${userId}/key-accounts`,
    requestNewZone: (zoneId: string) => `${USERS_V3_URL}users/zones/${zoneId}`,
    requestNewKeyAccount: (zoneId: string) =>
      `${USERS_V3_URL}users/zones/${zoneId}/key-accounts`,
  },
  experiments: {
    getExperiment: `${EXP_BASE_URL_V3}`,
    getExperimentV1: `${EXP_BASE_URL}`,
    getExperiments: `${EXP_BASE_URL}/experiments`,
    getExperimentsV3: `${EXP_BASE_URL_V3}/experiments`,
    dbconnectV1: `${BASE_URL}connect_db`,
    dbStatusV1: `${BASE_URL}db/status`,
    dbconnectV2: `${EXP_BASE_URL}/connect_db`,
    dbStatusV2: `${EXP_BASE_URL}/db/status`,
    createExperiment: `${BASE_URL}experiments`,
    uploadFile: `${BASE_URL}upload_file_experiment`,
    uploadFileV2: `${BASE_URL}/experiments/:experiment_id/file/:category`,
    columnMapping: `${BASE_URL}column_mapping`,
    validation: `${BASE_URL}validation`,
    delete: `${BASE_URL}experiments`,
    getprocess: `${EXPURL}process_by_experiment`,
    individualProcess: `${EXPURL}process`,
    download: `${EXPURL}download`,
    beforeEda: `${BASE_URL}standardize_column_names`,
    upload: `${EXPURL}upload`,
    updateState: `${EXPURL}process/state_name/`,
    states: `${EXPURL}states`,
    goProcess: `${GO_PROCESS_URL}`,
    goProcessStates: `${GOPROCESSSTATES}`,

    phoExperimentsFileUpload: `${PHOBASEURL}/experiments`,
    dtpFinancialImpact: (expId: string) =>
      `${PHOBASEURL}/${expId}/dtp/financial_impact`,
    checkDataAvailability: (expId: string, country: string) =>
      `${PHOBASEURL}/${expId}/dtp/data-availability?country=${country}`,
    getPHOPlans: `${EXP_BASE_URL_V3}/pins`,
    validateDTPPlan: (expId: string, country: string, category: string) =>
      `${PHOBASEURL}/${expId}/dtp/validate-linked-experiment?category=${category}&country=${country}`,
    saveDTPFile: (expId: string) =>
      `${PHOBASEURL}/${expId}/dtp/save-global-index`,
    submitDTPData: (expId: string) => `${PHOBASEURL}/${expId}/dtp/aggregate`,
    getVILCTable: (expId: string, flag: boolean) =>
      `${PHOBASEURL}/${expId}/dtp/sku-details?filter_flag=${flag}`,
    updateVILCForSKU: (expId: string) =>
      `${PHOBASEURL}/${expId}/dtp/sku-details`,
    getHistoryLogs: (expId: string, skuId: any) =>
      ` ${PHOBASEURL}/${expId}/dtp/sku-audit-log?sku=${skuId}`,
    getSimulationForDTP: (expId: string) =>
      `${PHOBASEURL}/${expId}/dtp/simulation`,
    refreshDTPData: (expId: string) => `${PHOBASEURL}/${expId}/dtp/refresh`,
    getLogisticsData: (expId: string, granularityLevel: string) =>
      `${PHOBASEURL}/${expId}/logistics?granularity_level=${granularityLevel}`,
    getLogisticsDataV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/logistics`,
    postLogisticsData: (expId: string) => `${PHOBASEURL}/${expId}/logistics`,
    postLogisticsDataV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/logistics`,
    phoExperiments: `${PHOBASEURL}/experiments`,
    phoDTPExperiments: `dtp/experiments`,
    phoBaseUrl: `${PHOBASEURL}`,
    postLevel0Pareto: (expId: string) => `${PHOBASEURL}/${expId}/level0/pareto`,
    postLevel0ParetoV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/level0/pareto`,
    getLevel0Pareto: (expId: string, granularityLevel: string) =>
      `${PHOBASEURL}/${expId}/level0/pareto?granularity_level=${granularityLevel}`,
    getLevel0ParetoV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/level0/pareto`,
    level0Refresh: (expId: string) => `${PHOBASEURL}/${expId}/level0/refresh`,
    level0ParetoRefresh: (expId: string) =>
      `${PHOBASEURL}/${expId}/level0/pareto/refresh`,
    postNestingCall: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/nesting/nesting`,
    refreshNestingValidation: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/refresh-validation`,
    logisticFrameWorkOutput: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/logistic-framework-output`,
    postNestingCallV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/nesting`,
    updateNestStatusV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/update-status`,
    nestingRefresh: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/nesting/refresh-nesting`,
    getSubLevelsV2: (expId: string, granularityLevel: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/sub-granularities?granularity_level=${granularityLevel}`,
    financialImpactProcessCall: (expId: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/process-mapping`,
    financialImpactCall: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/financial-impact`,
    financialImpactRefresh: (expId: string) =>
      `${PHOBASEURL_V3}/${expId}/financial-impact/refresh`,
    getFinancialImpactStatusCall: (expId: string) =>
      `${PHOBASEURL}/experiments/${expId}/has-sellout-data`,
    refreshMappingTable: (expId: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/refresh-mapping`,
    runDTPValidation: (expId: string, type: string) =>
      `${PHOBASEURL}/${expId}/dtp/validate/file/${type}`,
    runFIValidation: (expId: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/validate`,
    runFIValidationWithType: (expId: string, type: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/validate/${type}`,
    getSimulationSubLevelsV3: (expId: string, granularityLevel: string) =>
      `${PHOBASEURL_V3}/${expId}/simulation/sub-granularities?granularity_level=${granularityLevel}`,
    getSimulationResult: (expId: string, version: string) =>
      `${version === "V1" ? PHOBASEURL : version === "V2" ? PHOBASEURL_V2 : PHOBASEURL_V3}/${expId}/simulation/result`,
    getSimulationRefresh: (expId: string, version: string) =>
      `${version === "V2" ? PHOBASEURL_V2 : PHOBASEURL_V3}/${expId}/simulation/refresh-simulation`,
    getSimulationPHIData: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/simulation/extraction_report`,
    postSimulation: (expId: string) => `${PHOBASEURL_V2}/${expId}/simulation`,
    getSkuDetails: (expId: string, sku_id: string, isV3Experiment: boolean) =>
      `${isV3Experiment ? PHOBASEURL_V3 : PHOBASEURL}/${expId}/simulation/sku-details?sku_id=${sku_id}`,
    getSimulationSummary: (expId: string, version: string) =>
      `${version === "V1" ? PHOBASEURL : version === "V2" ? PHOBASEURL_V2 : PHOBASEURL_V3}/${expId}/simulation/simulation-summary`,
    getThresold: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/simulation/minimum-threshold`,
    createOptimization: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/optimization/pygad`,
    updateSimulation: (expId: string, version: string) =>
      `${version === "V1" ? PHOBASEURL : version === "V2" ? PHOBASEURL_V2 : PHOBASEURL_V3}/${expId}/simulation/update-output`,
    simulationReport: (expId: string) =>
      `${PHOBASEURL}/${expId}/simulation/report`,
    dtpFinancial: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/financial-impact/dtp-financial-impact`,
    financialImpactReset: (expId: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/reset/financial_impact`,
    simulatioUpdateResult: (expId: string) =>
      `${PHOBASEURL}/${expId}/simulation/update-result`,
    votingSummary: (expId: string) => `${PHOBASEURL}/${expId}/voting/summary`,
    votingDepartments: (expId: string) =>
      `${PHOBASEURL}/${expId}/voting/departments`,
    initiateVoting: (expId: string) => `${PHOBASEURL}/${expId}/voting/voting`,
    votingUsers: (expId: string) => `${PHOBASEURL}/${expId}/voting/users`,
    addUser: (expId: string) => `${PHOBASEURL}/${expId}/voting/users`,
    resetUser: (expId: string) => `${PHOBASEURL}/${expId}/voting/users/reset`,
    sendNotification: (expId: string) =>
      `${PHOBASEURL}/${expId}/voting/users/remind`,
    closeVoting: (expId: string) =>
      `${PHOBASEURL}/${expId}/voting/close-voting`,
    updateVoting: (expId: string) => `${PHOBASEURL}/${expId}/voting/voting`,
    submitVoting: (expId: string) =>
      `${PHOBASEURL}/${expId}/voting/vote/submit`,
    saveVoting: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/voting/vote/save`,
    simulationResult: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/voting/vote/simulation`,
    skuVoting: (expId: string, voterId: string) =>
      `${PHOBASEURL}/${expId}/voting/vote/skus?voter_id=${voterId}`,
    skuForSpr: (expId: string) => `${PHOBASEURL}/${expId}/spr/sku-for-spr`,
    innovationData: (expId: string) =>
      `${PHOBASEURL}/${expId}/spr/ppr_innovation_sku`,
    phiDetailsForSpr: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/spr/phi_details`,
    saveSprDecision: (expId: string) =>
      `${PHOBASEURL}/${expId}/spr/save-spr-decision`,
    concludeSpr: (expId: string) => `${PHOBASEURL}/${expId}/spr/conclude-spr`,
    summarySimulate: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/summary/simulate`,
    summaryPublish: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/summary/publish`,
    skuJourneyData: (expId: string) =>
      `${PHOBASEURL}/${expId}/summary/sku_journey`,
    sprSkuJourneyData: (expId: string) =>
      `${PHOBASEURL}/${expId}/spr/sku_journey`,
    excelDownload: (expId: string) => `${PHOBASEURL}/${expId}/summary/download`,
    skuDetailsDownload: (expId: string) =>
      `${PHOBASEURL}/${expId}/dashboard/report`,
    globalDashboardReport: (expId: string) =>
      `${PHOBASEURL}/${expId}/dashboard/extract_report`,
    shareExecutiveSummary: (expId: string) =>
      `${PHOBASEURL}/${expId}/summary/share`,
    sharePprSummary: (expId: string) => `${PHOBASEURL}/${expId}/spr/share`,
    phoSearchUser: `${USERS_V3_URL}users/search-entra-user`,
    downloadSprReport: (expId: string) => `${PHOBASEURL}/${expId}/spr/download`,
    financialImpactReport: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/financial-impact/download-report`,
    summarySellout: (expId: string) => `${PHOBASEURL}/${expId}/summary/sellout`,
    kpisAPI: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/spr/kpis`,
    sprSummary: (expId: string) => `${PHOBASEURL}/${expId}/spr/summary`,
    previousAnalysis: (expId: string) =>
      `${PHOBASEURL}/experiments/${expId}/previous-analysis`,
    summaryConclude: (expId: string, version: string) =>
      `${version === "V3" ? PHOBASEURL_V3 : PHOBASEURL}/${expId}/summary/conclude`,
    skuJourneyUpload: (expId: string) =>
      `${PHOBASEURL}/${expId}/summary/sku_journey/upload`,
    skuJourneyRefresh: (expId: string) =>
      `${PHOBASEURL}/${expId}/summary/sku_journey/refresh`,
    pprSkuJourneyRefresh: (expId: string) =>
      `${PHOBASEURL}/${expId}/spr/sku_journey/refresh`,

    affectedDashboard: (expId: string) =>
      `${PHOBASEURL}/${expId}/dashboard/affected-dashboard`,
  },
  dashboard: {
    getfilters: (expId: string) =>
      `${PHOBASEURL}/${expId}/dashboard/exp-details`,
    getSKUDetailsTablePagination: (
      expId: string,
      pageNumber: number,
      perPage: number,
    ) =>
      `${PHOBASEURL}/${expId}/dashboard/sku-details?page_number=${pageNumber}&per_page=${perPage}`,
    getFilterResult: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/dashboard/filter-result`,
    getDashboard: (expId: string, isOlderExperiment: boolean) =>
      `${isOlderExperiment ? PHOBASEURL : PHOBASEURL_V2}/${expId}/dashboard/result`,
  },
};

const status = [
  "started",
  "not_started",
  "pending",
  "in_progress",
  "completed",
  "success",
  "failed",
];
const xStatus = {
  started: "started",
  notStarted: "not_started",
  pending: "pending",
  inProgress: "in_progress",
  completed: "completed",
  failed: "failed",
  failure: "failure",
  success: "success",
};

@Injectable({
  providedIn: "root",
})
export class StateService {
  // unused temp/shared fields removed
  private level = new BehaviorSubject<any>(null);
  public Result = new BehaviorSubject<any>(null);
  private statesResetCall = new Subject<any>();
  private sidebarState = new Subject<any>();
  setFileName: Subject<any> = new Subject<any>();
  private dataSubject = new BehaviorSubject<any>(null);
  data$ = this.dataSubject.asObservable();
  readonly callSetLevel = this.level.asObservable();
  createNewExp = false;
  guideLayerShow = false;
  hashChanged = false;
  apiList = apiData;
  status = status;
  xStatus = xStatus;
  isDownload = false;
  calledApiList: any = [];
  // unused subjects / messages removed
  permissions = {
    tag_user_role: "users.roles.tag",
    untag_user_role: "users.roles.untag",

    admin_users_list: "admin.users.list",
    admin_requests_view: "admin.requests.view",
    admin_requests_approve: "admin.requests.approve",

    zone_users_list: "zones.users.list",
    zone_users_add: "zones.users.add",
    zone_users_migrate: "zones.users.migrate",
    zone_users_revoke: "zones.users.revoke",

    zone_users_key_account_sync: "zones.users.key-accounts.sync",
    zone_requests_view: "zones.requests.view",
    zone_requests_approve: "zones.requests.approve",

    zones_add: "zones.add",
    zones_edit: "zones.edit",
    zones_delete: "zones.delete",

    key_accounts_add: "key-accounts.add",
    key_accounts_edit: "key-accounts.edit",
    key_accounts_delete: "key-accounts.delete",

    roles_add: "roles.add",
    roles_edit: "roles.edit",
    roles_delete: "roles.delete",

    permissions_add: "permissions.add",
    permissions_edit: "permissions.edit",
    permissions_delete: "permissions.delete",
  };
  // removed large unused `process` and `searchCols` objects
  monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  // country/keyacc/region removed (not used)
  downloadProgressForSimulation = new BehaviorSubject<any>(null);
  openSoftWarningToast = new BehaviorSubject<any>(null);
  downloadTriggered = new Subject<any>();
  individualProcess: any;
  // removed unused PDF/pdfGeneration/zoom/assort fields
  levelsKeyOrder: any = ["Country", "State", "Region", "Channel"];
  private _lockAssortment$ = new BehaviorSubject(null);
  kbFactor = 1e3;
  mbFactor = 1e6;
  gbFactor = 1e9;
  allowedFileType: any = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/wps-office.xlsx",
    "text/csv",
  ];
  setStatesCall: boolean = false;
  setStatesList: any = [];
  experimentTypes: any = [
    "assortment",
    "space-assortment",
    "data-exploration",
    "clustering",
    "pho",
    "global-dashboard",
    "pho_dtp",
  ];

  constructor(
    private http: HttpService,
    private localStorageService: LocalStorageService,
  ) {}

  get getAssortmentLock(): any {
    return this._lockAssortment$;
  }

  setAssortmentLock(newValue: any) {
    this._lockAssortment$.next(newValue);
  }

  checkAppConfig() {
    return config?.app;
  }

  getAppDetails() {
    return config;
  }

  addCalledApi(url: string) {
    setTimeout(() => {
      this.calledApiList.push(url);
    }, 0);
  }

  removeCalledApi(url: string) {
    setTimeout(() => {
      this.calledApiList.splice(this.calledApiList.indexOf(url), 1);
    }, 0);
  }
  popOutCalledApi() {
    setTimeout(() => {
      this.calledApiList.pop();
    }, 0);
  }

  titleCase = (text: string = "") => {
    return (
      text &&
      typeof text === "string" &&
      text
        ?.split("_")
        .map((item) => item.charAt(0).toUpperCase() + item.substr(1))
        ?.join(" ")
    );
  };

  titleFirstLetterCaps = (text: string = "") =>
    text &&
    typeof text === "string" &&
    text
      .split("_")
      .map((item, index) =>
        index === 0 ? item.charAt(0).toUpperCase() + item.substr(1) : item,
      )
      ?.join(" ");

  expTypeTransform(value: string) {
    if (!value) {
      return "";
    }
    return value.replace(/[\s_]+/g, "-").toLowerCase();
  }

  nFormatter(num: number) {
    if (+num >= 1000000000) {
      return (+num / 1000000000).toFixed().replace(/\.0$/, "") + "B";
    }
    if (+num >= 1000000) {
      return (+num / 1000000).toFixed().replace(/\.0$/, "") + "M";
    }
    if (+num >= 1000) {
      return (+num / 1000).toFixed().replace(/\.0$/, "") + "K";
    }
    return num;
  }

  nFormatterDecimal(num: number) {
    const isNegative = num < 0;
    num = Math.abs(num); // Take the absolute value

    let formattedNum;
    if (num >= 1000000000) {
      formattedNum = (num / 1000000000).toFixed(1) + "B";
    } else if (num >= 1000000) {
      formattedNum = (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      formattedNum = (num / 1000).toFixed(1) + "K";
    } else {
      formattedNum = num.toString();
    }

    return isNegative ? "-" + formattedNum : formattedNum;
  }

  nFormatterVolume(num: number, type: string) {
    const isNegative = num < 0;
    num = Math.abs(num); // Take the absolute value

    let formattedNum;
    if (type === "Total Volume" || type === "Volume") {
      if (num >= 1000000000) {
        formattedNum =
          (num / 1000000000).toFixed().replace(/\.0$/, "") + "B HL";
      } else if (num >= 1000000) {
        formattedNum = (num / 1000000).toFixed().replace(/\.0$/, "") + "M HL";
      } else if (num >= 1000) {
        formattedNum = (num / 1000).toFixed().replace(/\.0$/, "") + "K HL";
      } else {
        formattedNum = num.toString();
      }
    } else {
      formattedNum = num.toString();
    }

    return isNegative ? "-" + formattedNum : formattedNum;
  }

  nFormatterVolumeDecimal(num: number, type: string) {
    const isNegative = num < 0;
    num = Math.abs(num); // Take the absolute value

    let formattedNum;
    if (type === "Total Volume") {
      if (num >= 1000000000) {
        formattedNum = (num / 1000000000).toFixed(1) + "B HL";
      } else if (num >= 1000000) {
        formattedNum = (num / 1000000).toFixed(1) + "M HL";
      } else if (num >= 1000) {
        formattedNum = (num / 1000).toFixed(1) + "K HL";
      } else {
        formattedNum = num.toString();
      }
    } else {
      formattedNum = num.toString();
    }

    return isNegative ? "-" + formattedNum : formattedNum;
  }

  nParseFloat(num: number) {
    return num?.toFixed(2);
  }

  setTokenExpiryDate() {
    window.localStorage.removeItem("tokenExpiryDate");

    const tokenExpiryDuration: number = ACCESS_TOKEN_EXPIRY_DURATION;
    const currentDate = new Date();
    const tokenExpiryDate = new Date(currentDate.getTime());

    tokenExpiryDate.setMinutes(currentDate.getMinutes() + tokenExpiryDuration);
    window.localStorage.setItem("tokenExpiryDate", tokenExpiryDate.toString());
  }

  getToken() {
    return window.localStorage.c;
  }
  openToastService(result: any) {
    this.Result.next(result);
  }
  setDownloadProgress(status: any) {
    this.downloadProgressForSimulation.next(status);
  }

  clearPlanoV2LocalStorage() {
    sessionStorage.removeItem("plansDetails");
    sessionStorage.removeItem("selectedPlanogramDetails");
    sessionStorage.removeItem("selectedPlanOfPlano");
    sessionStorage.removeItem("experimentMode");
  }

  getExistingPlan(url: any) {
    return this.http.getMethod(url);
  }

  //open Soft warning toast
  openSoftWarningToastService(message: any) {
    const marginClass = sessionStorage.getItem("toastMarginClass");
    const openToastInfo: any = {
      message: message,
      marginClass: marginClass,
    };
    this.openSoftWarningToast.next(openToastInfo);
  }
  getBulkUserDetailsService(payload: any) {
    const url = this.apiList.user.sharedWith;
    return this.http.postMethod(url, payload);
  }

  //terms and conditions
  acceptTermsAndondition(params: any) {
    const url = this.apiList.user.acceptTerms;
    return this.http.putMethod(url, params);
  }

  getExperimentDetailsByExpId() {
    const expId = sessionStorage.getItem("expId");
    const url = `${this.apiList.experiments.getExperiments}/${expId}`;
    return this.http.getMethod(url);
  }

  getSidebarStatesByExpId(refId?: any) {
    const expId = sessionStorage.getItem("expId");
    const url = `${this.apiList.experiments.goProcessStates}${refId ? refId : expId}/states`;
    return this.http.getMethod(url);
  }

  getFileName(filename: any, extension: any) {
    const expDetails = JSON.parse(sessionStorage.getItem("expDetails")!)[0];
    if (expDetails?.keyAccount) {
      expDetails.keyAccount = expDetails.keyAccount.split(" ").join("_");
    }
    expDetails.expName = expDetails.expName.split(" ").join("_");
    const date = new Date().toISOString().substring(0, 10).split("-").join("");
    const countryPrefix = expDetails?.expCountry
      ? `${expDetails.expCountry}_`
      : "";
    if (expDetails?.keyAccount) {
      expDetails.keyAccount = expDetails.keyAccount.split(" ").join("_");
      return (
        countryPrefix +
        expDetails.keyAccount +
        "_" +
        expDetails.expName +
        "_" +
        filename +
        "_" +
        date +
        "." +
        extension
      );
    } else {
      return (
        countryPrefix +
        expDetails.expName +
        "_" +
        filename +
        "_" +
        date +
        "." +
        extension
      );
    }
  }

  createProcessUrl(task_id: string) {
    return `${this.apiList.experiments.goProcess}/${task_id}/status`;
  }

  // Formatting logic for Volume
  formatVolumeLabel(num: number): string {
    if (num < 0) {
      // If the number is negative, remove the sign and format the absolute value
      return "- " + this.formatVolumeLabel(-num);
    }

    if (+num >= 1000000000 && +num <= 999999999999) {
      return (+num / 1000000000).toFixed(1) + "B HL";
    }
    if (+num >= 1000000 && +num < 999999999) {
      return (+num / 1000000).toFixed(1) + "M HL";
    }
    if (+num >= 1000 && +num < 999999) {
      return (+num / 1000).toFixed(1) + "K HL";
    }
    return `${num}`;
  }

  // Formatting logic for Volume
  formatTrendVolumeLabel(num: number): string {
    if (num < 0) {
      // If the number is negative, remove the sign and format the absolute value
      return "- " + this.formatRevenueLabel(-num);
    }
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(1)} B`;
    }
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)} M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)} K`;
    }
    return `${num}`;
  }

  // Formatting logic for Revenue and Maco
  formatRevenueLabel(num: any): string {
    if (num < 0) {
      // If the number is negative, remove the sign and format the absolute value
      return "- " + this.formatRevenueLabel(-num);
    }
    if (num >= 1000000000) {
      return `$${(num / 1000000000).toFixed(1)} B`;
    }
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)} M`;
    }
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)} K`;
    }
    return `$${num}`;
  }

  sendStatesCall(apiFlag: boolean) {
    this.statesResetCall.next(apiFlag);
  }

  getStatesCall() {
    return this.statesResetCall.asObservable();
  }

  updateSidebarState(value: boolean) {
    this.sidebarState.next(value);
  }

  isSidebarUpdated() {
    return this.sidebarState.asObservable();
  }
  getStatesList(states: any) {
    this.setStatesList = states;
  }

  checkStatesFlag(routerSlug: string) {
    this.setStatesList.map((item: any) => {
      if (item.slug === routerSlug) {
        this.setStatesCall = item.status === "completed" ? true : false;
      }
    });
    return this.setStatesCall;
  }

  parseFileSize(sizeInBytes: any) {
    const factor =
      sizeInBytes > this.gbFactor
        ? this.gbFactor
        : sizeInBytes > this.mbFactor
          ? this.mbFactor
          : this.kbFactor;
    const suffix =
      factor === this.gbFactor
        ? " GB"
        : factor === this.mbFactor
          ? " MB"
          : " KB";
    const size = sizeInBytes / factor;
    return String(parseFloat(size.toFixed(1))) + suffix;
  }

  checkExperimentType() {
    const expType = sessionStorage.getItem("expType") || "";
    return this.expTypeTransform(expType) || null;
  }

  //service to export json data to excel
  exportToExcel(excelHeader: any[], excelData: any[], fileName: string): void {
    let ws: XLSX.WorkSheet;
    const merges: XLSX.Range[] = [];
    switch (fileName) {
      case "summaryTable":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        merges.push(
          {
            s: { r: 0, c: 1 },
            e: { r: 0, c: 3 },
          },
          {
            s: { r: 0, c: 4 },
            e: { r: 0, c: 7 },
          },
        );
        break;

      case "portfolioHealthChart":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        merges.push(
          {
            s: { r: 0, c: 0 },
            e: { r: 0, c: 2 },
          },
          {
            s: { r: 0, c: 3 },
            e: { r: 0, c: 5 },
          },
        );
        break;

      case "portfolioEfficiency":
        // Ensure there's only one header row
        ws = XLSX.utils.aoa_to_sheet([excelHeader, ...excelData]);

        // No need to merge if the header already aligns correctly
        break;

      case "prePostportfolioRecommendation":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        const postRecommendationIndex = excelData.findIndex((row) =>
          row.includes("Post-Recommendation"),
        );
        merges.push(
          {
            s: { r: 0, c: 0 },
            e: { r: 0, c: 5 },
          },
          {
            s: { r: postRecommendationIndex, c: 0 },
            e: { r: postRecommendationIndex, c: 5 },
          },
        );
        break;

      case "skuJourneyTable":
        // Convert the object array to a 2D array
        const formattedData = excelData.map((item) => [
          item.name,
          item.y,
          item.y1,
        ]);

        // Check if formattedData is not empty
        if (!Array.isArray(formattedData) || formattedData.length === 0) {
          console.error("No data to export for skuJourneyTable.");
          return;
        }

        // Add header if needed (uncomment if you want headers)
        // const header = ['Name', 'Value'];
        // const dataWithHeader = [header, ...formattedData];

        ws = XLSX.utils.aoa_to_sheet(formattedData);
        break;

      // New case for tail analysis charts
      case "first-tail":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        // Add any specific merge logic if needed here
        break;

      case "second-tail":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        // Add any specific merge logic if needed here
        break;

      case "third-tail":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        // Add any specific merge logic if needed here
        break;

      case "fourth-tail":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        // Add any specific merge logic if needed here
        break;

      case "phi_bar_chart_data":
        ws = XLSX.utils.aoa_to_sheet([excelHeader, ...excelData]);
        break;

      default:
        const tableData = this.parseTableData(excelHeader, excelData);
        ws = XLSX.utils.aoa_to_sheet([tableData.header, ...tableData.data]);
        break;
    }

    if (merges.length > 0) {
      ws["!merges"] = merges;
    }
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  }

  parseTableData(excelHeader: any[], excelData: any[]) {
    const header = excelHeader.map((item) => [item.label]); // Build header array
    const data = excelData.map((item) =>
      excelHeader.map((entry) => item[entry.value]),
    ); // Build data array
    return { header, data };
  }

  sendData(data: any) {
    this.dataSubject.next(data);
  }

  // Helper function convert the number
  nParseFloatNumber(val: any) {
    if (typeof val === "string") {
      return val !== "" ? val : "-";
    } else if (this.isInt(val)) {
      return this.formatNumber(val);
    } else if (val && typeof val !== "string" && !this.isInt(val)) {
      const floatVal: any = this.nParseFloat(val);
      return this.formatNumber(floatVal);
    } else {
      return val || "-";
    }
  }

  // Helper function for check the number integer
  isInt(num: any) {
    return Number(num) === num && num % 1 === 0;
  }

  // Helper function for format the number
  formatNumber(num: number): string {
    if (typeof num !== "number" || !isFinite(num)) return "";
    const [integerPart, decimalPart] = Math.abs(num).toString().split(".");
    const safeInteger = integerPart.replace(/\d(?=(\d{3})+$)/g, "$&,");
    return (
      (num < 0 ? "-" : "") +
      safeInteger +
      (decimalPart ? "." + decimalPart : "")
    );
  }

  // Helper function for find the zone name
  getZoneName(zoneID: string, zoneList: any) {
    const item = zoneList.find(
      (item: any) => item.id === zoneID || item.name === zoneID,
    );
    return item?.name;
  }
  getGlobalDashboardData(expId: any, isOlderExperiment: boolean) {
    const url = this.apiList.dashboard.getDashboard(expId, isOlderExperiment);
    return this.http.getMethod(url);
  }

  getUserAccessType() {
    const userId = JSON.parse(sessionStorage.getItem("user-name")!)?.id;
    const userDetailsId = sessionStorage.getItem("user");
    const storedAccessType = sessionStorage.getItem("access_type");

    // If accessType already exists in session storage, return that.
    if (storedAccessType) {
      return storedAccessType;
    } else {
      // Otherwise, determine the access type and store it.
      const accessType = userId === userDetailsId ? "edit" : "read";
      sessionStorage.setItem("access_type", accessType);
      return accessType;
    }
  }

  // Helper method to getting the experiment details from session storage
  getExpDetails(): any | null {
    const expDetailsString = sessionStorage.getItem("expDetails");
    if (expDetailsString) {
      const expDetails = JSON.parse(expDetailsString);
      return Array.isArray(expDetails) ? expDetails[0] : null;
    }
    return null;
  }

  // Helper method to set the language
  setLanguageFeatures(languageTranslation: boolean) {
    const experimentalFeatures: any = {
      languageTranslate: languageTranslation,
    };
    this.localStorageService.setInfo(
      "experimentalFeatures",
      experimentalFeatures,
    );
  }
}
